import React, { useState, useEffect } from "react";
import Modal from "../Shared/Modal";
import { BunnyStorageObject } from "../../models/windowDisplay/WindowDisplayAd";

interface BunnyVideoSelectModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  videos: BunnyStorageObject[] | undefined;
  isLoading: boolean;
  onSelect: (videoUrl: string) => void;
}

export default function BunnyVideoSelectModal({
  open,
  setOpen,
  videos,
  isLoading,
  onSelect,
}: BunnyVideoSelectModalProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Reset search query when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
    }
  }, [open]);

  const getVideoUrl = (fullPath: string): string => {
    // Replace /estatetrack with https://estatetrack-forms.b-cdn.net
    return fullPath.replace("/estatetrack", "https://estatetrack-forms.b-cdn.net");
  };

  const handleVideoSelect = (video: BunnyStorageObject) => {
    const videoUrl = getVideoUrl(video.fullPath);
    onSelect(videoUrl);
    setOpen(false);
  };

  // Filter out directories and only show video files
  const allVideoFiles = videos?.filter((item) => !item.isDirectory) || [];
  
  // Filter videos based on search query
  const videoFiles = searchQuery
    ? allVideoFiles.filter((video) =>
        video.objectName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allVideoFiles;

  return (
    <Modal open={open} setOpen={setOpen} width={"max-w-5xl"}>
      <h3 className="text-lg px-2 leading-6 font-medium text-gray-900">
        Select Video from BunnyCDN
      </h3>
      <div className="mt-4 px-2">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search videos..."
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-etpink-500 focus:border-etpink-500 sm:text-sm"
        />
      </div>
      <div className="mt-2 p-2 max-h-[70vh] overflow-y-auto">
        {isLoading ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">Loading videos...</p>
          </div>
        ) : videoFiles.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">
              {searchQuery ? `No videos found matching "${searchQuery}"` : "No videos found"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {videoFiles.map((video) => (
              <div
                key={video.guid}
                onClick={() => handleVideoSelect(video)}
                className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-etpink-500 hover:shadow-md transition-all"
              >
                <div className="aspect-video bg-gray-100 rounded mb-2 flex items-center justify-center overflow-hidden">
                  <video
                    src={getVideoUrl(video.fullPath)}
                    className="max-w-full max-h-full rounded"
                    preload="metadata"
                    onError={(e) => {
                      // Hide video element if it fails to load
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
                <p className="text-sm font-medium text-gray-900 truncate" title={video.objectName}>
                  {video.objectName}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {(video.length / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

